import { STORAGE_KEYS, SUBSCRIPTION_TYPE, PLAN_STATUS } from 'const';
import { roundOff } from 'helpers/NumberFormatHelper';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
export const DEFAULT_ORDER_TYPES = ['DINE IN', 'TAKE AWAY', 'ONLINE', 'DELIVERY'];

function saveSelectedShopDetails(shop) {
  localStorage.setItem(STORAGE_KEYS.SHOP_ID, shop.id);
  localStorage.setItem(STORAGE_KEYS.SHOP_NAME, shop.shop_name);
  localStorage.setItem(STORAGE_KEYS.USER_TYPE, shop.subscription.type);
}

function getSubscriptionType(shop) {
  return String(shop?.subscription?.key || shop?.subscription?.type || '').toLowerCase();
}

function isFreePlan(shop) {
  return shop?.subscription?.type === SUBSCRIPTION_TYPE.FREE || shop?.plan_status === PLAN_STATUS.FREE;
}

function isBasicPlan(shop) {
  return isFreePlan(shop);
}

function isLitePlan(shop) {
  const type = getSubscriptionType(shop);
  return type.includes('lite') || type === SUBSCRIPTION_TYPE.BASIC || type === SUBSCRIPTION_TYPE.FREE;
}

function isProPlan(shop) {
  const type = getSubscriptionType(shop);
  return (
    type.includes('pro') ||
    type.includes('elite') ||
    type === SUBSCRIPTION_TYPE.STANDARD ||
    type === SUBSCRIPTION_TYPE.PREMIUM
  );
}

function isElitePlan(shop) {
  const type = getSubscriptionType(shop);
  return type.includes('elite') || type === SUBSCRIPTION_TYPE.PREMIUM;
}

function hasProAccess(shop) {
  if (!shop) return false;
  if (shop?.plan_status === PLAN_STATUS.TRIAL) return true;
  return isProPlan(shop);
}

function hasEliteAccess(shop) {
  if (!shop) return false;
  if (shop?.plan_status === PLAN_STATUS.TRIAL) return true;
  return isElitePlan(shop);
}

function hasPremiumAccess(shop) {
  return hasProAccess(shop);
}

function getAdEligibleUsers(shop) {
  return shop?.plan_status === PLAN_STATUS.FREE || shop?.plan_status === PLAN_STATUS.TRIAL;
}

function getSubscriptionRemainingDays(shop) {
  if (!shop) return;
  const subscriptionEndDate = new Date(shop.subscription_end_date);
  const milliSecondsPerDay = 1000 * 60 * 60 * 24;
  const subscriptionRemainingDays = (subscriptionEndDate.getTime() - new Date().getTime()) / milliSecondsPerDay;
  return Math.round(subscriptionRemainingDays).toFixed(0);
}

function getAmountDecimalCount(shop) {
  return shop?.tax_region?.toLowerCase() === 'oman' ? 3 : 2;
}

function getAmountFormatted(shop, amount) {
  return roundOff(amount, getAmountDecimalCount(shop));
}

function isAdSupportedPlan(shop) {
  return shop?.plan_status === PLAN_STATUS.FREE || shop?.plan_status === PLAN_STATUS.TRIAL;
}

function generateBatchName(date = new Date(), vendor = null) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');

  let base = `${y}/${m}/${d}-${hh}:${mm}`;
  if (vendor?.name) {
    base += `-${vendor.name.replace(/\s+/g, '_')}`;
  }
  return base;
}

function isWarehouseFeatureActive(shop) {
  return !featureIsExpired(shop?.warehouse_validity) && shop?.config?.is_warehouse_enabled;
}

function getDefaultOrderTypesConfig() {
  const defaultConfig = {};
  DEFAULT_ORDER_TYPES.forEach((type) => {
    defaultConfig[type] = {};
  });
  return defaultConfig;
}

function getOrderTypesConfig(shop) {
  const config = shop?.config || {};
  const orderTypes = config?.order_types || {};

  if (orderTypes && Object.keys(orderTypes).length > 0) {
    return orderTypes;
  }

  return getDefaultOrderTypesConfig();
}

function getLoyaltyPointValue(shop, points){
  return (Number(points) * Number(shop?.loyalty_point_conversion_rate || 1))
}

function isSwiggyEnabled(shop) {
  return Boolean(shop?.config?.is_swiggy_enabled);
}

function isZomatoEnabled(shop) {
  return Boolean(shop?.config?.is_zomato_enabled);
}

function isAnyExternalCartEnabled(shop) {
  return isSwiggyEnabled(shop) || isZomatoEnabled(shop);
}

function isShowProfitMarginAndPriceInPurchase(shop) {
  const val = shop?.config?.show_profit_margin_and_price_in_purchase;
  return val === undefined || val === null ? true : Boolean(val);
}

const ShopsHelper = {
  saveSelectedShopDetails,
  getSubscriptionRemainingDays,
  getAmountDecimalCount,
  getAmountFormatted,
  hasPremiumAccess,
  isFreePlan,
  isBasicPlan,
  isLitePlan,
  isProPlan,
  isElitePlan,
  hasProAccess,
  hasEliteAccess,
  getSubscriptionType,
  isAdSupportedPlan,
  getAdEligibleUsers,
  generateBatchName,
  isWarehouseFeatureActive,
  getDefaultOrderTypesConfig,
  getDefaultOrderTypeConfig: getDefaultOrderTypesConfig,
  getOrderTypesConfig,
  getOrderTypeConfig: getOrderTypesConfig,
  getOrderTypes: getOrderTypesConfig,
  getLoyaltyPointValue,
  isSwiggyEnabled,
  is_swiggy_enabled: isSwiggyEnabled,
  isZomatoEnabled,
  is_zomato_enabled: isZomatoEnabled,
  isAnyExternalCartEnabled,
  is_any_external_cart_enable: isAnyExternalCartEnabled,
  is_any_external_cart_enabled: isAnyExternalCartEnabled,
  isAnyExternalCartEnable: isAnyExternalCartEnabled,
  isShowProfitMarginAndPriceInPurchase,
};

export default ShopsHelper;


